import { alpha, createTheme } from "@mui/material/styles";
import { deDE as gridDeDe } from "@mui/x-data-grid-premium/locales";
import { deDE as pickersDeDe } from "@mui/x-date-pickers/locales";
import { deDE as coreDeDe } from "@mui/material/locale";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";

declare module "@mui/material/styles" {
  interface Theme {
    paperTableContainer: {
      maxHeight: string;
    };
    hoveredOrSelectedCell: {
      backgroundColor: string;
    };
    oddNumberedRow: {
      backgroundColor: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    paperTableContainer?: {
      maxHeight?: string;
    };
    hoveredOrSelectedCell?: {
      backgroundColor?: string;
    };
    oddNumberedRow?: {
      backgroundColor?: string;
    };
  }
}

export const transnetTheme = createTheme(
  {
    palette: {
      mode: "light",
      primary: {
        main: "#004E59",
        light: "#1A606A",
        dark: "#33717A",
        contrastText: "#5C1616",
      },
      secondary: {
        main: "#BEBD00",
        light: "#B0BB1D",
        dark: "#C0C94A",
        contrastText: "#FFFFFF",
      },
      text: {
        primary: "#231F20",
        secondary: "#004E59",
        disabled: "#7B7979",
      },
      error: {
        main: "#B90000",
        light: "#FFCBCD",
      },
      warning: {
        main: "#ED6E00",
        light: "#FEE1B3",
      },
      info: {
        main: "#177992",
        light: "#B4E6F8",
      },
      success: {
        main: "#9A9A2E",
        contrastText: "#231F20",
        light: "#F2F2CC",
      },
      divider: "#A7A5A6",
      background: {
        default: "#FBFBF3",
      },
    },
    typography: {
      fontFamily: ["Avenir Next LT Pro", "Arial"].join(","),
      fontSize: 12,
    },
    spacing: 4,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            position: "sticky",
            backgroundColor: theme.palette.primary.main,
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.dense && {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.secondary.contrastText,
              "&.active": { color: theme.palette.secondary.contrastText, backgroundColor: theme.palette.primary.dark },
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }),
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "white",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            backgroundColor: "#E6E598",
            "&.Mui-selected": {
              backgroundColor: "white",
              color: theme.palette.text.secondary,
            },
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
        },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.color === "primary" &&
              ownerState.variant === "contained" && {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.secondary.contrastText,
                "&:hover": {
                  // backgroundColor: theme.palette.primary.dark,
                },
              }),
            ...(ownerState.color === "secondary" &&
              ownerState.variant === "contained" && {
                backgroundColor: theme.palette.secondary.contrastText,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: "#F2F6F6",
                },
              }),
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.color === "primary"
              ? {
                  backgroundColor: "#CCDCDE",
                  color: "#4F4C4D",
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.secondary.contrastText,
                  },
                }
              : {
                  backgroundColor: "#EFF1D2",
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.secondary.light,
                    color: theme.palette.secondary.contrastText,
                  },
                }),
          }),
        },
      },
      MuiTable: { defaultProps: { size: "small" } },
      MuiTableRow: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "&:nth-of-type(even)": {
              backgroundColor: "#F2F6F6",
            },
            "&:nth-of-type(odd)": {
              backgroundColor: "#FFFFFF",
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            border: "0.5px solid #7AA2A7",
          }),
          head: ({ ownerState, theme }) => ({
            backgroundColor: "#CCDCDE",
            color: theme.palette.primary.main,
            fontWeight: 700,
          }),
        },
      },
      MuiDataGrid: {
        defaultProps: {
          density: "compact",
          getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"),
        },
        styleOverrides: {
          row: ({ ownerState, theme }) => ({
            "&.odd": {
              backgroundColor: "#F2F6F6",
              "&:hover, &.Mui-hovered": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                "@media (hover: none)": {
                  backgroundColor: "transparent",
                },
              },
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2 + theme.palette.action.selectedOpacity),
                "&:hover, &.Mui-hovered": {
                  backgroundColor: alpha(
                    theme.palette.primary.main,
                    0.2 + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
                  ),
                  // Reset on touch devices, it doesn't add specificity
                  "@media (hover: none)": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2 + theme.palette.action.selectedOpacity),
                  },
                },
              },
            },
            "&.even": {
              backgroundColor: "#FFFFFF",
              "&:hover, &.Mui-hovered": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                "@media (hover: none)": {
                  backgroundColor: "transparent",
                },
              },
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2 + theme.palette.action.selectedOpacity),
                "&:hover, &.Mui-hovered": {
                  backgroundColor: alpha(
                    theme.palette.primary.main,
                    0.2 + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
                  ),
                  // Reset on touch devices, it doesn't add specificity
                  "@media (hover: none)": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2 + theme.palette.action.selectedOpacity),
                  },
                },
              },
            },
          }),
          columnHeader: ({ ownerState, theme }) => ({
            backgroundColor: "#CCDCDE",
            border: "0.5px solid #7AA2A7",
            color: theme.palette.primary.main,
            fontWeight: 700,
          }),
          columnHeaderTitle: ({ ownerState, theme }) => ({
            color: theme.palette.primary.main,
            fontWeight: 700,
            fontSize: "0.875rem",
          }),
          cell: ({ ownerState, theme }) => ({
            border: "0.5px solid #7AA2A7",
          }),
          footerContainer: ({ ownerState, theme }) => ({
            backgroundColor: "#CCDCDE",
            border: "0.5px solid #7AA2A7",
          }),
        },
      },
    },
    paperTableContainer: { maxHeight: "44vh" },
    hoveredOrSelectedCell: { backgroundColor: "#ddd" },
    oddNumberedRow: { backgroundColor: "#eee" },
  },
  gridDeDe,
  pickersDeDe,
  coreDeDe
);
